import { DateHelper } from '@connect-soft/date-helper';
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import { styled } from '@mui/material/styles';
import { useEffect, useMemo } from 'react';
import { Checkbox } from '@/fields/Checkbox';
import { TextField } from '@/fields/TextField';
import { TimePicker } from '@/fields/TimePicker';
import { normalizeNumber } from '@/utils/helpers/number';
import { ITimeRangePickerProps, TimeRangePicker } from '../TimeRangePicker';

export type ITimeRangeOrTimeAndLengthValueType = ITimeRangePickerProps['value'] & {
    isRange?: boolean;
};
export type ITimeRangeOrTimeAndLengthProps = Omit<
    ITimeRangePickerProps,
    'label' | 'value' | 'minTime' | 'maxTime' | 'error' | 'helperText' | 'zeroIsNextDay' | 'onError' | 'onChange'
> & {
    name: string;
    disabled?: boolean;
    readOnly?: boolean;
    label:
        | string
        | {
              start: string;
              end: string;
              length: string;
          };
    labelRange: string;
    value?: ITimeRangeOrTimeAndLengthValueType;
    inputFormat?: string;
    required?: boolean;
    minutesStep?: number;
    minStart?: ITimeRangeOrTimeAndLengthValueType['start'];
    maxDuration?: number;
    error?: boolean | { time?: boolean; duration?: boolean };
    helperText?: string | { time?: string; duration?: string };
    onChange?: (value: ITimeRangeOrTimeAndLengthValueType) => void;
    onError?: (value: { time?: string; duration?: string }) => void;
};

const StyledWrapper = styled(Box)`
    display: grid;
    grid-template-columns: 1fr max-content;
`;
const StyledLength = styled(Box)(({ hidden }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    ...(hidden && { display: 'none' })
}));
const StyledSign = styled(Box, {
    shouldForwardProp: (propName) => 'size' !== propName
})<{
    size: ITimeRangePickerProps['size'];
}>(({ theme, hidden, size }) => ({
    margin: `${theme.spacing(1)} ${theme.spacing(1.7)} 0`,
    ...(size === 'small' && { fontSize: '1rem' }),
    ...(hidden && { display: 'none' })
}));
const StyledTextField = styled(TextField)`
    flex: 1;
`;
const StyledCheckbox = styled(FormControlLabel)(
    ({ theme }) => `
        margin-left: ${theme.spacing(1)};
        align-items: flex-start;

        .MuiFormControlLabel-label {
            margin-top: ${theme.spacing(1)};
        }
    `
);

export function TimeRangeOrTimeAndLength({
    name,
    label,
    labelRange,
    value,
    disabled,
    readOnly,
    required,
    minutesStep,
    minStart,
    maxDuration,
    error,
    helperText,
    onError,
    onChange,
    ...rest
}: ITimeRangeOrTimeAndLengthProps) {
    const validValue = useMemo(
        () => ({
            start: value?.start ?? null,
            duration: value?.duration ?? 0,
            isRange: value?.isRange
        }),
        [value]
    );
    const hasLimits = minStart && maxDuration;
    const minDuration = 0.25;
    const maxStart = minStart && maxDuration ? DateHelper.from(minStart).addHours(maxDuration ?? 0, true) : undefined;
    const maxValidStart =
        minStart && maxDuration ? DateHelper.from(minStart).addHours(maxDuration - minDuration) : undefined;
    const maxDurationSource =
        validValue.start && maxStart
            ? DateHelper.from(validValue.start).diff(maxStart, 'hours')
            : DateHelper.from(validValue.start).diff(DateHelper.from(validValue.start).addDays(1), 'hours');
    const maxValidDuration = Math.round(maxDurationSource / minDuration) * minDuration;

    useEffect(() => {
        if (onChange && !validValue.isRange && !validValue.start && hasLimits) {
            onChange({
                ...validValue,
                start: minStart
            });
        }
    }, [validValue, hasLimits, minStart, onChange]);

    return (
        <>
            {typeof label === 'string' && <FormLabel required={required}>{label}</FormLabel>}
            <StyledWrapper data-testid={`time_range_or_time_and_length_${name}`}>
                {validValue.isRange ? (
                    <TimeRangePicker
                        label={
                            typeof label === 'string' ? undefined : { start: label?.start ?? '', end: label?.end ?? '' }
                        }
                        disabled={disabled}
                        readOnly={readOnly}
                        required={required}
                        name={name}
                        minutesStep={minutesStep}
                        value={validValue}
                        error={
                            typeof error === 'boolean'
                                ? error
                                : {
                                      start: !!error?.time,
                                      end: !!error?.duration
                                  }
                        }
                        helperText={
                            typeof helperText === 'string'
                                ? undefined
                                : {
                                      start: helperText?.time || '',
                                      end: helperText?.duration || ''
                                  }
                        }
                        minTime={hasLimits ? minStart : undefined}
                        maxTime={hasLimits ? DateHelper.addHours(validValue.start, maxValidDuration) : undefined}
                        onError={({ start, end }) => onError && onError({ time: start, duration: end })}
                        onChange={(newValue) =>
                            onChange &&
                            onChange({
                                isRange: true,
                                ...newValue
                            })
                        }
                    />
                ) : (
                    <StyledLength>
                        <TimePicker
                            {...rest}
                            name={`${name}.start`}
                            label={typeof label === 'string' ? undefined : label?.start}
                            // inputFormat={inputFormat}
                            value={validValue.start}
                            readOnly={readOnly}
                            disabled={disabled}
                            required={required}
                            minutesStep={minutesStep}
                            minTime={minStart ?? undefined}
                            maxTime={maxValidStart?.getObj() ?? undefined}
                            error={typeof error === 'boolean' ? error : !!error?.time}
                            helperText={typeof helperText === 'string' ? undefined : helperText?.time}
                            onError={(message) => onError && onError({ time: message })}
                            onChange={(newValue) => {
                                if (onChange) {
                                    if (validValue.isRange) {
                                        const diff =
                                            DateHelper.from(newValue).diff(validValue.start, 'hours') +
                                            parseFloat(`${validValue.duration ?? '0.0'}`);

                                        onChange({
                                            ...validValue,
                                            start: newValue,
                                            duration: normalizeNumber(diff, (minutesStep ?? 60) / 60)
                                        });
                                    } else {
                                        onChange({
                                            ...validValue,
                                            start: newValue
                                        });
                                    }
                                }
                            }}
                        />
                        <StyledSign size={rest.size}>+</StyledSign>
                        <StyledTextField
                            name={`${name}.duration`}
                            data-testid={`text_field_${name}.duration`}
                            required={required}
                            label={typeof label === 'string' ? undefined : label?.length}
                            type="number"
                            readOnly={readOnly}
                            disabled={disabled}
                            value={validValue.duration}
                            error={typeof error === 'boolean' ? error : !!error?.duration}
                            helperText={typeof helperText === 'string' ? undefined : helperText?.duration}
                            sx={{ flex: 1 }}
                            onChange={(event) => {
                                const parsed = parseFloat(event.target.value);

                                return (
                                    onChange &&
                                    onChange({
                                        ...validValue,
                                        duration: Number.isNaN(parsed) ? null : parsed
                                    })
                                );
                            }}
                            slotProps={{
                                input: {
                                    inputProps: {
                                        min: minDuration,
                                        max: hasLimits && validValue.start && maxValidDuration ? maxValidDuration : 24,
                                        step: 0.25
                                    },
                                    endAdornment: 'h'
                                }
                            }}
                        />
                    </StyledLength>
                )}
                <StyledCheckbox
                    disabled={disabled}
                    value={!!value?.isRange}
                    control={<Checkbox name={name} value={!!value?.isRange} />}
                    label={labelRange}
                    onChange={(_, newValue) =>
                        !readOnly &&
                        onChange &&
                        onChange({
                            ...validValue,
                            isRange: Boolean(newValue)
                        })
                    }
                />
            </StyledWrapper>
            {typeof helperText === 'string' && (
                <FormHelperText error={typeof error === 'boolean' && error}>{helperText}</FormHelperText>
            )}
        </>
    );
}
